import { useQuery } from '@tanstack/react-query'
import { Box, Callout, Flex, Select, Spinner, Text } from '@radix-ui/themes'
import { SectionCard } from '~/components/SectionCard'
import { listDataSources } from '~/features/stations/datasources-service'
import { useTranslation } from 'react-i18next'
import { WaterStation } from '~/features/stations/schemas'
import { HiOutlineInformationCircle } from 'react-icons/hi2'
import { useState, useRef } from 'react'
import { StationLeakChart, ViewMode, ViewModes } from '~/features/stations/components/StationLeakChart'
import { StationsConsumptionChart } from '~/features/dashboarding/components/StationsConsumptionChart'
import { PeriodSelector, PeriodSelection, useDefaultDateRange } from '~/components/PeriodSelector'
import { DateRange } from '~/types'
import ResolutionSelector from '~/components/ResolutionSelector'
import { Resolution } from '@willie/schemas'
import {
  ConsumptionChartType,
  ConsumptionChartTypePicker
} from '~/features/dashboarding/components/ChartTypePicker'
import { ViewOptionsPopover } from '~/features/dashboarding/components/ViewOptionsPopover'
import { FieldWrapper } from '~/components/FormFieldWrapper'
import { useChartExportCSV, ChartExportHandle } from '~/features/dashboarding/hooks/useChartExportCSV'
import { useChartExportImage } from '~/features/dashboarding/hooks/useChartExportImage'
import { ChartSectionMoreOptionsMenu } from '~/features/dashboarding/components/MoreOptionsDropdownMenu'

type ConsumptionAndLeaksSectionProps = {
  station: WaterStation
  stationNameAsTitle?: boolean
}

const ConsumptionAndLeaksSection = ({ station, stationNameAsTitle }: ConsumptionAndLeaksSectionProps) => {
  const { t } = useTranslation('sites')

  // Simple local state for period and date range
  const [periodSelection, setPeriodSelection] = useState<PeriodSelection>('week')
  const [resolution, setResolution] = useState<Resolution>('day')
  const [dateRange, setDateRange] = useState<DateRange>(useDefaultDateRange('week'))
  const [chartType, setChartType] = useState<ConsumptionChartType>('area')
  const [viewMode, setViewMode] = useState<ViewMode>('partition')
  const chartRef = useRef<ChartExportHandle>(null as any)

  const handlePeriodChange = (newDateRange: DateRange, newPeriod: PeriodSelection) => {
    setDateRange(newDateRange)
    setPeriodSelection(newPeriod)
  }

  const { data: datasources = [], isLoading } = useQuery({
    queryKey: ['station-datasources', station.id],
    queryFn: () => listDataSources({ stationID: station.id })
  })

  const hasShaypMeter = datasources.some(
    (datasource) => datasource.sourceType === 'meter' && datasource.config.maker === 'shayp'
  )

  const exportFilename = `${station.name}_${dateRange.startDate.toISODate()}_${dateRange.endDate.toISODate()}`
  const handleExportCSV = useChartExportCSV(chartRef, `${exportFilename}.csv`)
  const { ref: imageExportRef, handleExportImage, isExporting } = useChartExportImage(`${exportFilename}.png`)

  if (isLoading) {
    return (
      <Flex align={'center'} justify={'center'} className="min-h-32">
        <Spinner />
      </Flex>
    )
  }

  if (datasources.length === 0) {
    return null
  }

  return (
    <SectionCard
      title={stationNameAsTitle ? station.name : t('stations.leakSection.title')}
      innerRef={imageExportRef}
      secondaryActions={
        <Flex gap={'2'} className="no-export">
          <ViewOptionsPopover>
            <FieldWrapper label={'Période'}>
              <PeriodSelector
                size={'2'}
                periodSelection={periodSelection}
                dateRange={dateRange}
                onPeriodChange={handlePeriodChange}
              />
            </FieldWrapper>
            <FieldWrapper label={'Résolution'}>
              <ResolutionSelector size={'2'} defaultResolution={resolution} onChange={setResolution} />
            </FieldWrapper>
            <FieldWrapper label={'Type de graphique'}>
              <ConsumptionChartTypePicker
                selectRootProps={{ size: '2' }}
                defaultType={'area'}
                onTypeChange={(type) => setChartType(type)}
              />
            </FieldWrapper>
            {hasShaypMeter && (
              <FieldWrapper label={t('stations.leakSection.chart.viewMode')}>
                <Select.Root value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)} size="2">
                  <Select.Trigger />
                  <Select.Content color={'gray'} variant="soft">
                    {ViewModes.map((mode) => (
                      <Select.Item key={mode} value={mode}>
                        {t(`stations.leakSection.chart.${mode}`)}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </FieldWrapper>
            )}
          </ViewOptionsPopover>
          <ChartSectionMoreOptionsMenu
            handleExportCSV={handleExportCSV}
            handleExportImage={handleExportImage}
            isExporting={isExporting}
            linkTo={'/sites/$siteID/config/stations/$stationID'}
            linkParams={{ siteID: station.site_id, stationID: station.id }}
            linkText={t('stations.leakSection.chart.settings')}
          />
        </Flex>
      }
    >
      {!hasShaypMeter && (
        <Callout.Root className="no-export">
          <Callout.Icon>
            <HiOutlineInformationCircle size={18} />
          </Callout.Icon>
          <Callout.Text>
            <Text as="p">{t('stations.leakSection.messages.notShaypWarning')}</Text>
          </Callout.Text>
        </Callout.Root>
      )}

      <Box py="4">
        {hasShaypMeter ? (
          <StationLeakChart
            ref={chartRef}
            title={''}
            station={station}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            resolution={resolution}
            chartType={chartType}
            viewMode={viewMode}
          />
        ) : (
          <StationsConsumptionChart
            ref={chartRef}
            title={''}
            yAxisLabel={t('stations.leakSection.chart.yAxisLabel')}
            partitionKey={null}
            showGridLines={false}
            chartType={chartType}
            stations={[station]}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        )}
      </Box>
    </SectionCard>
  )
}

export { ConsumptionAndLeaksSection }
